import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./index.module.css";
import MultiselectDropdown from "../../elements/MultiselectDropdown";
import {
  categorySteps,
  conditionSteps,
  conditionMapping,
} from "../../../utils/constant";
import DropDown from "../../elements/Dropdown";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import PriceChangeTable from "../../elements/PriceChangeTable";
import { rowCounts } from "../../../utils/constant";
import { useSelector, useDispatch } from "react-redux";
import Header from "../../elements/Header";
import { MultiSelect } from 'primereact/multiselect';
import { PrimeReactProvider } from 'primereact/api';
import 'primereact/resources/themes/lara-light-cyan/theme.css';
import 'primereact/resources/primereact.min.css';
import {
  fetchLivePriceChangeAsyncThunk,
  setLivePriceChange,
  setSelectedCategories,
  setSelectedBrands,
  setSelectedLocations,
  setSelectedCondition,
  setConditionValue,
  setDateRange,
  setSelRowCount,
  setSelPage,
  setSearch,
} from "../../../store/slices/Products/livePriceChange.slice";
import { getSource, filterLocationsByOrg } from "../../../utils/helpers";
import ExportButton from '../../elements/ExportButton';

const LiveMonitoring = () => {
  const dispatch = useDispatch();

  // selectors
  const brandSteps = useSelector((store) => store.constant.brand) || [];
  const locationSteps = useSelector((store) => store.constant.location || []);
  const organizationId = useSelector((store) => store.user.organizationId);

  const sourceData = useSelector((store) => store.constant.source);
  const priceChangeData = useSelector((store) => store.livePrice.priceChange);
  const closeCalendar = useSelector((store) => store.toggleDropdown.calendar);

  // Get filter state from Redux
  const filterState = useSelector((s) => s.livePrice.filters || {});
  const loadingLocation = useSelector((s) => s.constant.loadingLocation);

  const [dateToggle, setDateToggle] = useState({ start: false, end: false });
  const [isInitialized, setIsInitialized] = useState(false);
  const [filterKey, setFilterKey] = useState(0);
  const [totalRecords, setTotalRecords] = useState(null);

  // Filter locations based on organization ID (hide Adelaide and Cairns for org ID 3)
  const filteredLocationSteps = useMemo(() => {
    return filterLocationsByOrg(locationSteps, organizationId);
  }, [locationSteps, organizationId]);

  // build local locationOptions (includes current source)
  const locationOptions = useMemo(() => {
    const list = Array.isArray(filteredLocationSteps) ? [...filteredLocationSteps] : [];
    if (sourceData) list.push(sourceData);
    const dedup = Array.from(new Set(list)).filter(Boolean);
    return dedup;
  }, [filteredLocationSteps, sourceData]);

  // helpers
  const getThreeMonthsAgo = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
  };
  const getToday = () => new Date();

  // initial local state (prefer persisted filters; if absent set later after loadingLocation false)
  const [selectedCategories, setLocalSelectedCategories] = useState(
    filterState.selectedCategories && filterState.selectedCategories.length > 0
      ? filterState.selectedCategories
      : categorySteps || []
  );
  const [selectedBrands, setLocalSelectedBrands] = useState(
    filterState.selectedBrands && filterState.selectedBrands.length > 0 ? filterState.selectedBrands : []
  );
  const [selectedLocations, setLocalSelectedLocations] = useState(
    filterState.selectedLocations && filterState.selectedLocations.length > 0 ? filterState.selectedLocations : []
  );

  const [selectedCondition, setLocalSelectedCondition] = useState(
    filterState.selectedCondition !== undefined ? filterState.selectedCondition : 0
  );
  const [dateRange, setLocalDateRange] = useState({ start: "", end: "" });
  const [selPage, setLocalSelPage] = useState(filterState.selPage !== undefined ? filterState.selPage : 0);

  const [value, onChange] = useState({
    startValue: filterState.dateRange?.startValue ? new Date(filterState.dateRange.startValue) : getThreeMonthsAgo(),
    endValue: filterState.dateRange?.endValue ? new Date(filterState.dateRange.endValue) : getToday(),
  });

  const [selRowCount, setLocalSelRowCount] = useState(filterState.selRowCount !== undefined ? filterState.selRowCount : 0);
  const [conditionValue, setLocalConditionValue] = useState(filterState.conditionValue || "");
  const [search, setLocalSearch] = useState(filterState.search || "");
  const [brandSearch, setBrandSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');

  const arraysEqual = (a = [], b = []) => {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (String(a[i]) !== String(b[i])) return false;
    return true;
  };

  // Debug: Log state changes
  console.log('LiveMonitoring render - selPage:', selPage, 'totalRecords:', totalRecords, 'priceChangeData length:', priceChangeData?.length);

  // Debounce function to prevent multiple API calls
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };
  // Get live price data.
  const getLivePriceChange = async ({
    limit,
    offset,
    source,
    category,
    brand,
    start_date,
    end_date,
    locations,
    action,
    diff_value,
    search: searchParam,
  } = {}) => {
    const finalLimit = limit || rowCounts[selRowCount];
    const finalOffset = offset !== undefined ? offset : selPage * Number(rowCounts[selRowCount]);
    const finalSource = source || sourceData;
    const finalCategory = category || selectedCategories.join(",").toLowerCase();
    const finalBrand = brand || selectedBrands.join(",").toLowerCase();
    const finalStartDate = start_date || value.startValue.toLocaleDateString("en-CA");
    const finalEndDate = end_date || value.endValue.toLocaleDateString("en-CA");

    const finalLocations =
      locations !== undefined
        ? locations
        : selectedLocations && selectedLocations.length > 0
          ? selectedLocations.join(",").toLowerCase()
          : (locationOptions && locationOptions.length > 0 ? locationOptions.join(",").toLowerCase() : "");

    const finalAction = action || conditionMapping[conditionSteps[selectedCondition]];
    const finalDiffValue = diff_value !== undefined ? diff_value : (conditionValue || null);
    const finalSearch = searchParam !== undefined ? searchParam : (search || null);

    const payload = {
      limit: finalLimit,
      offset: finalOffset,
      category: finalCategory,
      source: finalSource,
      start_date: finalStartDate,
      end_date: finalEndDate,
      action: finalAction,
      brand: finalBrand,
      diff_value: finalDiffValue,
      search: finalSearch,
    };
    if (finalLocations && finalLocations.length > 0) payload.locations = finalLocations;
    const response = await dispatch(fetchLivePriceChangeAsyncThunk(payload));
    if (response?.payload?.status === "success") {
      setTotalRecords(response.payload.totals);
      dispatch(setLivePriceChange(response.payload.data));
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      setIsInitialized(true);
    }, 120);
    return () => clearTimeout(t);
  }, []);

  // initialize selectedBrands/selectedLocations only AFTER loadingLocation === false and isInitialized
  useEffect(() => {
    if (!isInitialized) return;
    if (loadingLocation) {
      return;
    }
    
    let brandsUpdated = false;
    let locationsUpdated = false;
    
    const hasPersistedBrands = filterState.selectedBrands && filterState.selectedBrands.length > 0;
    if (!hasPersistedBrands && (selectedBrands.length === 0) && brandSteps && brandSteps.length > 0) {
      setLocalSelectedBrands(brandSteps);
      dispatch(setSelectedBrands(brandSteps));
      brandsUpdated = true;
    } else if (hasPersistedBrands && !arraysEqual(selectedBrands, filterState.selectedBrands)) {
      setLocalSelectedBrands(filterState.selectedBrands);
    }

    const hasPersistedLocations = filterState.selectedLocations && filterState.selectedLocations.length > 0;
    if (!hasPersistedLocations && (selectedLocations.length === 0) && locationOptions && locationOptions.length > 0) {
      setLocalSelectedLocations(locationOptions);
      dispatch(setSelectedLocations(locationOptions));
      locationsUpdated = true;
    } else if (hasPersistedLocations && !arraysEqual(selectedLocations, filterState.selectedLocations)) {
      setLocalSelectedLocations(filterState.selectedLocations);
    }

    // If we just auto-selected brands or locations, trigger API call
    if ((brandsUpdated || locationsUpdated) && brandSteps && brandSteps.length > 0 && locationOptions && locationOptions.length > 0) {
      const finalBrands = brandsUpdated ? brandSteps : selectedBrands;
      const finalLocations = locationsUpdated ? locationOptions : selectedLocations;
      
      if (finalBrands.length > 0 && finalLocations.length > 0) {
        getLivePriceChange({
          category: selectedCategories.join(",").toLowerCase(),
          brand: finalBrands.join(",").toLowerCase(),
          locations: finalLocations.join(",").toLowerCase(),
          action: conditionMapping[conditionSteps[selectedCondition]],
          diff_value: conditionValue || null,
          search: search || null,
        });
      }
    }
  }, [isInitialized, loadingLocation, locationOptions, brandSteps]);

  // debounced search
  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      const currentLocations = (filterState.selectedLocations && filterState.selectedLocations.length > 0)
        ? filterState.selectedLocations
        : (selectedLocations && selectedLocations.length > 0 ? selectedLocations : locationOptions);

      getLivePriceChange({
        category: selectedCategories.join(",").toLowerCase(),
        brand: selectedBrands.join(",").toLowerCase(),
        locations: currentLocations && currentLocations.length > 0 ? currentLocations.join(",").toLowerCase() : undefined,
        action: conditionMapping[conditionSteps[selectedCondition]],
        diff_value: conditionValue || null,
        search: searchValue || null,
      });
    }, 500),
    [selectedCategories, selectedBrands, selectedLocations, selectedCondition, conditionValue, filterState.selectedLocations, locationOptions]
  );

  // Handle search input changes
  const handleSearchChange = (e) => {
    const searchValue = e.target.value;
    setLocalSearch(searchValue);
    dispatch(setSearch(searchValue));

    // Call debounced API with search
    debouncedSearch(searchValue);
  };

  // Handle source change - reset all filters
  const handleSourceChange = (newSource) => {
    // Increment key to force re-render of all filter components
    setFilterKey((prev) => prev + 1);

    setLocalSelectedCategories(categorySteps || []);
    setLocalSelectedBrands(brandSteps || []);
    setLocalSelectedLocations(locationOptions || []);
    setLocalSelectedCondition(0);
    setLocalConditionValue("");
    setLocalSearch("");
    setLocalSelRowCount(0);
    setLocalSelPage(0);

    // Reset date range to 3 months ago
    const threeMonthsAgo = getThreeMonthsAgo();
    const today = getToday();
    onChange({ startValue: threeMonthsAgo, endValue: today });
    setLocalDateRange({
      start: threeMonthsAgo.toLocaleDateString("en-GB"),
      end: today.toLocaleDateString("en-GB"),
    });
    // Reset Redux states
    dispatch(setSelectedCategories(categorySteps || []));
    dispatch(setSelectedBrands(brandSteps || []));
    dispatch(setSelectedLocations(locationOptions || []));
    dispatch(setSelectedCondition(0));
    dispatch(setConditionValue(""));
    dispatch(setSearch(""));
    dispatch(setSelRowCount(0));
    dispatch(setSelPage(0));
    dispatch(setDateRange({
      startValue: threeMonthsAgo.toISOString(),
      endValue: today.toISOString(),
    }));

    getLivePriceChange({
      limit: rowCounts[0],
      offset: 0,
      start_date: threeMonthsAgo.toLocaleDateString("en-CA"),
      end_date: today.toLocaleDateString("en-CA"),
      locations: (locationOptions && locationOptions.length > 0) ? locationOptions.join(",").toLowerCase() : undefined,
      source: newSource,
    });
  };

  useEffect(() => {
    if (!isInitialized) return;
    if (loadingLocation) {
      return;
    }

    // Only fetch data if we have brands, locations, and categories loaded
    // AND we have selected brands and locations (auto-selected or persisted)
    if (brandSteps && locationOptions && categorySteps && 
        selectedBrands.length > 0 && selectedLocations.length > 0) {
      if (priceChangeData === -1 || (Array.isArray(priceChangeData) && priceChangeData.length === 0)) {
        const currentLocations = (filterState.selectedLocations && filterState.selectedLocations.length > 0)
          ? filterState.selectedLocations
          : (selectedLocations && selectedLocations.length > 0 ? selectedLocations : locationOptions);

        getLivePriceChange({
          category: selectedCategories.join(",").toLowerCase(),
          brand: selectedBrands.join(",").toLowerCase(),
          locations: currentLocations && currentLocations.length > 0 ? currentLocations.join(",").toLowerCase() : undefined,
          action: conditionMapping[conditionSteps[selectedCondition]],
          diff_value: conditionValue || null,
          search: search || null,
        });
      }
    }
  }, [isInitialized, loadingLocation, brandSteps, locationOptions, categorySteps, selectedBrands, selectedLocations, selectedCategories, selectedCondition, conditionValue, search]);

  // close calendar effect
  useEffect(() => {
    setDateToggle({ start: false, end: false });
  }, [closeCalendar]);

  // date range handler
  const handleDateRange = (e) => {
    const localDate = new Date(e);
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, "0");
    const day = String(localDate.getDate()).padStart(2, "0");
    const format = localDate.toLocaleDateString("en-GB");

    const currentLocations = (filterState.selectedLocations && filterState.selectedLocations.length > 0)
      ? filterState.selectedLocations
      : (selectedLocations && selectedLocations.length > 0 ? selectedLocations : locationOptions);

    if (dateToggle.start) {
      onChange({ ...value, startValue: e });
      setLocalDateRange({ ...dateRange, start: format });
      dispatch(setDateRange({ startValue: e.toISOString(), endValue: value.endValue.toISOString() }));

      getLivePriceChange({
        start_date: `${year}-${month}-${day}`,
        end_date: value.endValue.toLocaleDateString("en-CA"),
        category: selectedCategories.join(",").toLowerCase(),
        brand: selectedBrands.join(",").toLowerCase(),
        locations: currentLocations && currentLocations.length > 0 ? currentLocations.join(",").toLowerCase() : undefined,
        action: conditionMapping[conditionSteps[selectedCondition]],
        diff_value: conditionValue || null,
        search: search || null,
      });
    }

    if (dateToggle.end) {
      onChange({ ...value, endValue: e });
      setLocalDateRange({ ...dateRange, end: format });
      dispatch(setDateRange({ startValue: value.startValue.toISOString(), endValue: e.toISOString() }));

      getLivePriceChange({
        start_date: value.startValue.toLocaleDateString("en-CA"),
        end_date: `${year}-${month}-${day}`,
        category: selectedCategories.join(",").toLowerCase(),
        brand: selectedBrands.join(",").toLowerCase(),
        locations: currentLocations && currentLocations.length > 0 ? currentLocations.join(",").toLowerCase() : undefined,
        action: conditionMapping[conditionSteps[selectedCondition]],
        diff_value: conditionValue || null,
        search: search || null,
      });
    }

    setDateToggle({ start: false, end: false });
  };

  useEffect(() => {
    setLocalDateRange({
      start: value.startValue.toLocaleDateString("en-GB"),
      end: value.endValue.toLocaleDateString("en-GB"),
    });
  }, [value.startValue, value.endValue]);

  // Format label helper functions
  const formatBrandLabel = (brand) => {
    return brand && brand.includes("_") ? brand.replace(/_/g, " ") : brand;
  };

  const formatLocationLabel = (loc) => {
    if (loc === "aelia_auckland") return "auckland dutyfree";
    return loc && loc.includes("_") ? loc.replace(/_/g, " ") : loc;
  };

  // Brand options with "All"
  const allBrandOptions = useMemo(() => {
    if (!brandSteps || brandSteps.length === 0) return [{ name: "All", code: "__ALL__" }];
    const options = brandSteps.map(brand => ({
      name: formatBrandLabel(brand),
      code: brand
    }));
    return [{ name: "All", code: "__ALL__" }, ...options];
  }, [brandSteps]);

  const brandOptions = useMemo(() => {
    if (!brandSearch || brandSearch.trim() === '') {
      return allBrandOptions;
    }
    
    const searchTerm = brandSearch.toLowerCase();
    const filtered = [allBrandOptions[0]]; 
    for (let i = 1; i < allBrandOptions.length; i++) {
      const option = allBrandOptions[i];
      if (option.name.toLowerCase().includes(searchTerm)) {
        filtered.push(option);
      }
    }
    
    return filtered;
  }, [allBrandOptions, brandSearch]);

  // Location options with "All"
  const allLocationOptions = useMemo(() => {
    if (!locationOptions || locationOptions.length === 0) return [{ name: "All", code: "__ALL__" }];
    const options = locationOptions.map(loc => ({
      name: formatLocationLabel(loc),
      code: loc
    }));
    return [{ name: "All", code: "__ALL__" }, ...options];
  }, [locationOptions]);

  const locationOptionsForSelect = useMemo(() => {
    if (!locationSearch || locationSearch.trim() === '') {
      return allLocationOptions;
    }
    
    const searchTerm = locationSearch.toLowerCase();
    const filtered = [allLocationOptions[0]]; 
    for (let i = 1; i < allLocationOptions.length; i++) {
      const option = allLocationOptions[i];
      if (option.name.toLowerCase().includes(searchTerm)) {
        filtered.push(option);
      }
    }
    
    return filtered;
  }, [allLocationOptions, locationSearch]);

  const allBrandsSelected = selectedBrands.length === (brandSteps?.length || 0) && (brandSteps?.length || 0) > 0;
  const allLocationsSelected = selectedLocations.length === (locationOptions?.length || 0) && (locationOptions?.length || 0) > 0;
  
  const selectedBrandsWithAll = useMemo(() => {
    if (allBrandsSelected) {
      return ["__ALL__", ...selectedBrands];
    }
    return selectedBrands;
  }, [allBrandsSelected, selectedBrands]);

  const selectedLocationsWithAll = useMemo(() => {
    if (allLocationsSelected) {
      return ["__ALL__", ...selectedLocations];
    }
    return selectedLocations;
  }, [allLocationsSelected, selectedLocations]);

  // selection handlers with logs
  const onSelectedCategoryChange = (value) => {
    if (!isInitialized) {
      return;
    }
    setLocalSelectedCategories(value);
    dispatch(setSelectedCategories(value));

    const currentLocations = (filterState.selectedLocations && filterState.selectedLocations.length > 0)
      ? filterState.selectedLocations
      : (selectedLocations && selectedLocations.length > 0 ? selectedLocations : locationOptions);

    getLivePriceChange({
      category: value.join(",").toLowerCase(),
      brand: selectedBrands.join(",").toLowerCase(),
      locations: currentLocations && currentLocations.length > 0 ? currentLocations.join(",").toLowerCase() : undefined,
      action: conditionMapping[conditionSteps[selectedCondition]],
      diff_value: conditionValue || null,
      search: search || null,
    });
  };

  const onSelectedBrandChange = (e) => {
    if (!isInitialized) {
      return;
    }
    const selectedCodes = e.value || [];
    
    // Check if "All" is in the new selection
    const isAllSelected = selectedCodes.includes("__ALL__");
    const wasAllSelected = selectedBrandsWithAll.includes("__ALL__");
    
    let filteredValue;
    if (isAllSelected !== wasAllSelected) {
      if (isAllSelected) {
        filteredValue = brandSteps || [];
      } else {
        filteredValue = [];
      }
    } else {
      filteredValue = selectedCodes.filter(brand => brand !== "__ALL__");
    }
    
    setLocalSelectedBrands(filteredValue);
    dispatch(setSelectedBrands(filteredValue));

    const currentLocations = (filterState.selectedLocations && filterState.selectedLocations.length > 0)
      ? filterState.selectedLocations
      : (selectedLocations && selectedLocations.length > 0 ? selectedLocations : locationOptions);

    getLivePriceChange({
      category: selectedCategories.join(",").toLowerCase(),
      brand: filteredValue.join(",").toLowerCase(),
      locations: currentLocations && currentLocations.length > 0 ? currentLocations.join(",").toLowerCase() : undefined,
      action: conditionMapping[conditionSteps[selectedCondition]],
      diff_value: conditionValue || null,
      search: search || null,
    });
  };

  const onSelectedLocationChange = (e) => {
    if (!isInitialized) {
      return;
    }
    const selectedCodes = e.value || [];
    
    // Check if "All" is in the new selection
    const isAllSelected = selectedCodes.includes("__ALL__");
    const wasAllSelected = selectedLocationsWithAll.includes("__ALL__");
    
    let filteredValue;
    if (isAllSelected !== wasAllSelected) {
      if (isAllSelected) {
        filteredValue = locationOptions || [];
      } else {
        filteredValue = [];
      }
    } else {
      filteredValue = selectedCodes.filter(loc => loc !== "__ALL__");
    }
    
    setLocalSelectedLocations(filteredValue);
    dispatch(setSelectedLocations(filteredValue));

    getLivePriceChange({
      category: selectedCategories.join(",").toLowerCase(),
      brand: selectedBrands.join(",").toLowerCase(),
      locations: filteredValue && filteredValue.length > 0 ? filteredValue.join(",").toLowerCase() : undefined,
      action: conditionMapping[conditionSteps[selectedCondition]],
      diff_value: conditionValue || null,
      search: search || null,
    });
  };

  const onSelectedCondition = (value, index) => {
    setLocalSelectedCondition(index);
    dispatch(setSelectedCondition(index));
    fetchDataOnConditionChange(conditionSteps[index], conditionValue);
  };

  const onSelectedRowCount = (value, index) => {
    setLocalSelRowCount(index);
    dispatch(setSelRowCount(index));

    const currentLocations = (filterState.selectedLocations && filterState.selectedLocations.length > 0)
      ? filterState.selectedLocations
      : (selectedLocations && selectedLocations.length > 0 ? selectedLocations : locationOptions);

    getLivePriceChange({
      limit: rowCounts[index],
      offset: selPage * rowCounts[index],
      category: selectedCategories.join(",").toLowerCase(),
      brand: selectedBrands.join(",").toLowerCase(),
      locations: currentLocations && currentLocations.length > 0 ? currentLocations.join(",").toLowerCase() : undefined,
      action: conditionMapping[conditionSteps[selectedCondition]],
      diff_value: conditionValue || null,
      search: search || null,
    });
  };

  function fetchDataOnConditionChange(condition, data) {
    const currentLocations = (filterState.selectedLocations && filterState.selectedLocations.length > 0)
      ? filterState.selectedLocations
      : (selectedLocations && selectedLocations.length > 0 ? selectedLocations : locationOptions);

    if (condition !== "none") {
      let action = null;
      switch (condition) {
        case "==": action = "equal"; break;
        case "<": action = "less_than"; break;
        case "<=": action = "less_than_equal"; break;
        case ">": action = "greater_than"; break;
        case ">=": action = "greater_than_equal"; break;
      }
      getLivePriceChange({
        category: selectedCategories.join(",").toLowerCase(),
        brand: selectedBrands.join(",").toLowerCase(),
        locations: currentLocations && currentLocations.length > 0 ? currentLocations.join(",").toLowerCase() : undefined,
        action,
        diff_value: data,
        search: search || null,
      });
    } else {
      setLocalConditionValue("");
      getLivePriceChange({
        category: selectedCategories.join(",").toLowerCase(),
        brand: selectedBrands.join(",").toLowerCase(),
        locations: currentLocations && currentLocations.length > 0 ? currentLocations.join(",").toLowerCase() : undefined,
        action: null,
        diff_value: null,
        search: search || null,
      });
    }
  }

  const handleConditionValue = (e) => {
    setLocalConditionValue(e.target.value);
    dispatch(setConditionValue(e.target.value));
    fetchDataOnConditionChange(conditionSteps[selectedCondition], e.target.value);
  };

  const handlePage = (page) => {
    const currentLocations = (filterState.selectedLocations && filterState.selectedLocations.length > 0)
      ? filterState.selectedLocations
      : (selectedLocations && selectedLocations.length > 0 ? selectedLocations : locationOptions);

    getLivePriceChange({
      limit: rowCounts[selRowCount],
      offset: page * rowCounts[selRowCount],
      category: selectedCategories.join(",").toLowerCase(),
      brand: selectedBrands.join(",").toLowerCase(),
      locations: currentLocations && currentLocations.length > 0 ? currentLocations.join(",").toLowerCase() : undefined,
      action: conditionMapping[conditionSteps[selectedCondition]],
      diff_value: conditionValue || null,
      search: search || null,
    });
  };

  const onPageNoChange = (e) => {
    const page = parseInt(e?.currentTarget?.dataset?.idx);
    handlePage(page);
    setLocalSelPage(page);
    dispatch(setSelPage(page));
  };

  const skipPages = () => {
    const page = (parseInt(selPage / 5) + 1) * 5;
    handlePage(page);
    setLocalSelPage(page);
    dispatch(setSelPage(page));
  };

  const skipPagesReverse = () => {
    const page = (parseInt(selPage / 5) - 1) * 5;
    handlePage(page);
    setLocalSelPage(page);
    dispatch(setSelPage(page));
  };

  if (!brandSteps || !filteredLocationSteps || !categorySteps) {
    return (
      <div className={styles["container"]}>
        <Header isProducts={true} onSearchChange={handleSearchChange} searchValue={search} onSourceChange={handleSourceChange} />
        <hr />
        <div style={{ textAlign: "center", padding: "50px" }}>
          <p>Loading filters...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles["container"]}>
      <Header isProducts={true} onSearchChange={handleSearchChange} searchValue={search} onSourceChange={handleSourceChange} />
      <hr />

      <div className={styles["filters"]}>
        <div>
          <div className={styles["filter"]}>
            <div>
              <div>Category</div>
              <div><i className="bi bi-columns-gap"></i></div>
            </div>
            <div className={styles["dropdown"]}>
              <MultiselectDropdown key={`category-${filterKey}`} steps={categorySteps || []} selSteps={selectedCategories} onChange={(v) => onSelectedCategoryChange(v)} name="" />
            </div>
          </div>

          <div className={styles["filter"] + " " + styles["brandFilter"]}>
            <div>
              <div>Brand</div>
              <div><i className="bi bi-tag"></i></div>
            </div>
            <div className={styles["dropdown"]}>
              {brandSteps && brandSteps.length > 0 && (
                <PrimeReactProvider value={{ hideOverlaysOnDocumentScrolling: true }}>
                  <div className="brand-multiselect-wrapper">
                    <MultiSelect
                      value={selectedBrandsWithAll}
                      onChange={onSelectedBrandChange}
                      options={brandOptions}
                      optionLabel="name"
                      optionValue="code"
                      placeholder={selectedBrands.length === 0 ? "0" : "Select Brands"}
                      maxSelectedLabels={0}
                      selectedItemsLabel={allBrandsSelected ? "All" : selectedBrands.length > 0 ? `${selectedBrands.length}` : "0"}
                      className="brand-multiselect"
                      panelClassName="brand-multiselect-panel"
                      filter={false}
                      showClear={false}
                      appendTo="self"
                      panelHeaderTemplate={() => (
                        <input
                          type="text"
                          placeholder="Search"
                          value={brandSearch}
                          onChange={(e) => setBrandSearch(e.target.value)}
                          className="brand-search-input"
                          style={{
                            width: '100%',
                            marginBottom: '8px',
                            padding: '6px',
                            fontSize: '1.6rem',
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                            boxSizing: 'border-box'
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                        />
                      )}
                      emptyMessage=""
                    />
                  </div>
                </PrimeReactProvider>
              )}
            </div>
          </div>

          <div className={styles["filter"] + " " + styles["locationFilter"]}>
            <div>
              <div>Location</div>
              <div><i className="bi bi-pin"></i></div>
            </div>
            <div className={styles["dropdown"]}>
              {locationOptions && locationOptions.length > 0 && (
                <PrimeReactProvider value={{ hideOverlaysOnDocumentScrolling: true }}>
                  <div className="location-multiselect-wrapper">
                    <MultiSelect
                      value={selectedLocationsWithAll}
                      onChange={onSelectedLocationChange}
                      options={locationOptionsForSelect}
                      optionLabel="name"
                      optionValue="code"
                      placeholder={selectedLocations.length === 0 ? "0" : "Select Locations"}
                      maxSelectedLabels={0}
                      selectedItemsLabel={allLocationsSelected ? "All" : selectedLocations.length > 0 ? `${selectedLocations.length}` : "0"}
                      className="location-multiselect"
                      panelClassName="location-multiselect-panel"
                      filter={false}
                      showClear={false}
                      appendTo="self"
                      panelHeaderTemplate={() => (
                        <input
                          type="text"
                          placeholder="Search"
                          value={locationSearch}
                          onChange={(e) => setLocationSearch(e.target.value)}
                          className="location-search-input"
                          style={{
                            width: '100%',
                            marginBottom: '8px',
                            padding: '6px',
                            fontSize: '1.6rem',
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                            boxSizing: 'border-box'
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                        />
                      )}
                      emptyMessage=""
                    />
                  </div>
                </PrimeReactProvider>
              )}
            </div>
          </div>

          <div className={styles["filter"] + " " + styles["condition"]}>
            <div>
              <div>Condition</div>
              <div><i className="bi bi-card-checklist"></i></div>
            </div>
            <div className={styles["dropdownPercent"]}>
              <div className={styles["dropdown"]}>
                <DropDown steps={conditionSteps} selectedValue={selectedCondition} onChange={onSelectedCondition} />
              </div>
              <div className={styles["percent"]}>
                <input type="text" placeholder="1.50" value={conditionValue} onChange={handleConditionValue} />
                <span>%</span>
              </div>
            </div>
          </div>

          <div className={styles["dateRange"]}>
            <span>Date Range</span>
            <div className={styles["dates"]}>
              <div className={styles["start"]} onClick={(e) => { e.stopPropagation(); setDateToggle({ start: !dateToggle.start, end: false }); }}>
                <input type="text" defaultValue={dateRange?.start} readOnly />
                <i className="bi bi-calendar-date"></i>
              </div>
              <div className={styles["end"]} onClick={(e) => { e.stopPropagation(); setDateToggle({ start: false, end: !dateToggle.end }); }}>
                <input type="text" defaultValue={dateRange?.end} readOnly />
                <i className="bi bi-calendar-date"></i>
              </div>
            </div>
            {dateToggle.start && (<div className={styles["calendar"]} onClick={(e) => e.stopPropagation()}><Calendar value={value.startValue} onChange={(e) => handleDateRange(e)} /></div>)}
            {dateToggle.end && (<div className={styles["calendar"]} onClick={(e) => e.stopPropagation()}><Calendar value={value.endValue} onChange={(e) => handleDateRange(e)} /></div>)}
          </div>
        </div>
      </div>

      <hr />

      <div className={styles["priceChanges"]}>
        {/* heading */}
        <div className={styles["heading"]}>
          <span>Price changes</span>
        </div>

        {/* products */}

        <div className={styles["products"]}>
          <PriceChangeTable
            data={priceChangeData != -1 ? priceChangeData : []}
          />
        </div>
      </div>

      <div className={styles["rowCount"]}>
        <div className={styles["count"]}>
          <span>Row Count</span>
          <div className={styles["countDropdown"]}><DropDown steps={rowCounts} selectedValue={selRowCount} onChange={onSelectedRowCount} /></div>
        </div>

        <div className={styles["pages"]}>
          {selPage < 5 ? null : <span onClick={skipPagesReverse}>&lt;</span>}
          {new Array(parseInt(totalRecords / rowCounts[selRowCount]) + 1 || 0).fill(1).map((el, idx) =>
            idx >= parseInt(selPage / 5) * 5 && idx < parseInt(selPage / 5 + 1) * 5 ? (
              <span key={idx} className={selPage == idx ? styles["selected"] : ""} onClick={onPageNoChange} data-idx={idx}>{idx + 1}</span>
            ) : null
          )}
          {totalRecords / rowCounts[selRowCount] <= (parseInt(selPage / 5) + 1) * 5 ? null : <span onClick={skipPages}>&gt;</span>}
        </div>
      </div>
    </div>
  );
};

export default LiveMonitoring;
